import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import IconButton from "../../common/IconButton/IconButton";
import PlusIcon from "../../../assets/PlusIcon";
import ModalComponent from "../../common/ModalComponent/ModalComponent";
import { DataTableComponent } from "../../common/TableComponent/DataTableComponent";
import Loader from "../../common/Loader/Loader";
import {
  getAllDomains,
  getDomainById,
  createDomain,
  updateDomain,
} from "../../../services/foundation/domain-service";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const DomainManagement = () => {
  const { t } = useTranslation();
  const [domains, setDomains] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("add");
  const [formData, setFormData] = useState({
    domainName: "",
    status: "Active",
    comments: "",
  });
  const [editIndex, setEditIndex] = useState(null);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Fetch all domains
  const fetchDomains = async () => {
  setLoading(true);
  try {
    const response = await getAllDomains();
    const apiData = response?.data || []; // <-- FIXED
    const mappedDomains = apiData.map((item) => ({
      id: item.id,
      domainName: item.domainAddress,
      status: item.isActive ? "Active" : "De-Active",
      comments: item.comment,
      addedBy: item.createdBy,
      lastModifiedDate: item.lastModifiedDate || "",
    }));
    setDomains(mappedDomains);
  } catch (err) {
    setDomains([]);
    console.error("Error fetching domains:", err);
  } finally {
    setLoading(false);
  }
};

  useEffect(() => {
    fetchDomains();
  }, []);

  // Open modal for add, edit, or view
  const handleOpenModal = async (mode = "add", data = null, index = null) => {
  setModalMode(mode);
  if (mode === "add") {
    setFormData({
      domainName: "",
      status: "Active",
      comments: "",
    });
    setEditIndex(null);
    setIsModalOpen(true);
  } else if (data) {
    if (mode === "view" && data.id) {
      setLoading(true);
      try {
        const response = await getDomainById(data.id);
        const domain = response?.data; // <-- FIXED
        setFormData({
          domainName: domain?.domainAddress || "",
          status: domain?.isActive ? "Active" : "De-Active",
          comments: domain?.comment || "",
        });
      } catch (err) {
        console.error("Error fetching domain details:", err);
      } finally {
        setLoading(false);
      }
    } else {
      setFormData({
        domainName: data.domainName,
        status: data.status,
        comments: data.comments,
      });
    }
    setEditIndex(index);
    setIsModalOpen(true);
  }
};

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditIndex(null);
  };

  const handleInputChange = (target) => {
    const { name, value } = target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Add or Edit Domain
  const handleAddOrEditDomain = async () => {
    if (!formData.domainName.trim()) {
      toast.error(t("domainNameRequired"));
      return;
    }
    setLoading(true);
    try {
      const payload = {
        domainAddress: formData.domainName,
        isActive: formData.status === "Active",
        comment: formData.comments,
      };

      if (modalMode === "add") {
        await createDomain(payload);
      } else if (modalMode === "edit" && editIndex !== null) {
        const domainId = domains[editIndex]?.id;
        await updateDomain(domainId, payload);
      }
      await fetchDomains();
      handleCloseModal();
    } catch (err) {
      console.error("Error saving domain:", err);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      name: t("domainName"),
      field: "domainName",
      selector: (row) => row.domainName,
      sortable: true,
    },
    {
      name: t("addedBy"),
      field: "addedBy",
      selector: (row) => row.addedBy,
      sortable: true,
    },
    {
      name: t("lastModifiedDate"),
      field: "lastModifiedDate",
      selector: (row) => row.lastModifiedDate,
      sortable: true,
    },
    {
      name: t("status"),
      field: "status",
      selector: (row) => row.status,
      sortable: true,
    },
  ];

  return (
    <div className="group-management-container">
      <div className="surveyHeader">
        <h2 className="surveyTitle">{t("listOfDomains")}</h2>
        <IconButton
          text={t("addDomain")}
          onClick={() => handleOpenModal("add")}
          style={{
            backgroundColor: "#F0F0F0",
            color: "#454647",
            border: "1px solid #B3B2B4",
          }}
          icon={<PlusIcon />}
        />
      </div>
      <hr />
      <div style={{ marginTop: "1rem" }}>
        {loading ? (
          <Loader />
        ) : (
          <DataTableComponent
            columns={columns}
            data={domains}
            tableName="DomainManagement"
            pageCount={5}
            onEditDetails={(row, index) => {
              handleOpenModal("edit", row, index);
            }}
            handleView={(row, index) => {
              handleOpenModal("view", row, index);
            }}
          />
        )}
      </div>
      <ModalComponent
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        modalTitle={
          modalMode === "add"
            ? t("addDomain")
            : modalMode === "edit"
            ? t("editDomain")
            : t("viewDomainDetails")
        }
        fieldsData={[
          {
            fieldName: t("domainName"),
            name: "domainName",
            type: "input",
           readOnly: modalMode === "view",
            required: true,
          },
          {
            fieldName: t("status"),
            name: "status",
            type: "dropdown",
            options: [
              { label: t("active"), value: "Active" },
              { label: t("deActive"), value: "De-Active" },
            ],
            isFieldDisabled: modalMode === "view",
          },
          {
            fieldName: t("comments"),
            name: "comments",
            type: "textarea",
            isFieldDisabled: modalMode === "view",
          },
        ]}
        formData={formData}
        onChangeInput={handleInputChange}
        buttons={
          modalMode === "view"
            ? [
                <button
                  key="close"
                  className="cancelButton"
                  onClick={handleCloseModal}
                >
                 {t("close")}
                </button>,
              ]
            : [
                <button
                  key="save"
                  className="saveButton"
                  onClick={handleAddOrEditDomain}
                >
                  {modalMode === "edit" ? t("update") : t("add")}
                </button>,
                <button
                  key="cancel"
                  className="cancelButton"
                  onClick={handleCloseModal}
                >
                  {t("cancel")}
                </button>,
              ]
        }
      />
    </div>
  );
};

export default DomainManagement;